import type { AtomRecord } from "../model/atom.js";
import type { SuggestedAction } from "../model/evaluation.js";
import type { BoardState } from "../model/position.js";
import { computeStateHash } from "./hash.js";
import { recordProof, selectLine, upsertAtom, upsertCell } from "./mutations.js";
import { evaluateBoardState, type EvaluationDetail } from "./evaluate.js";

const BEAM_WIDTH = 3;
const RESPONSE_LIMIT = 6;

type PlannedAction = {
  action: SuggestedAction;
  apply: (state: BoardState, helpers: VirtualMutationHelpers) => void;
};

type PlannerStats = {
  exploredBranches: number;
  transpositionHits: number;
  visitedNodes: number;
};

type VirtualMutationHelpers = {
  allocateAtomId(requestedId: string | undefined): string;
  allocateCellId(requestedId: string | undefined): string;
};

type ScoredAction = {
  action: SuggestedAction;
  score: number;
};

export function planSuggestedActions(
  sessionKey: string,
  state: BoardState,
  detail: EvaluationDetail,
  depth: number,
) {
  const stats: PlannerStats = {
    exploredBranches: 0,
    transpositionHits: 0,
    visitedNodes: 0,
  };
  const cache = new Map<string, number>();
  const actions = enumerateActions(sessionKey, state, detail);
  const ranked = actions
    .map((plannedAction) => scoreAction(plannedAction, state, depth, sessionKey, stats, cache))
    .sort((left, right) =>
      right.score - left.score ||
      left.action.kind.localeCompare(right.action.kind) ||
      left.action.reason.localeCompare(right.action.reason)
    )
    .slice(0, RESPONSE_LIMIT);

  return {
    responses: ranked.map((entry) => entry.action),
    stats,
  };
}

function scoreAction(
  plannedAction: PlannedAction,
  state: BoardState,
  depth: number,
  sessionKey: string,
  stats: PlannerStats,
  cache: Map<string, number>,
): ScoredAction {
  const nextState = structuredClone(state);
  const helpers = createVirtualMutationHelpers(nextState);
  plannedAction.apply(nextState, helpers);
  const score = scoreState(nextState, depth - 1, sessionKey, stats, cache);
  return {
    action: {
      ...plannedAction.action,
      previewPhase: evaluateBoardState(nextState).evaluation.phase,
      score,
    },
    score,
  };
}

function scoreState(
  state: BoardState,
  depth: number,
  sessionKey: string,
  stats: PlannerStats,
  cache: Map<string, number>,
): number {
  const key = `${computeStateHash(state)}:${depth}`;
  const cached = cache.get(key);
  if (cached !== undefined) {
    stats.transpositionHits += 1;
    return cached;
  }

  stats.visitedNodes += 1;
  const detail = evaluateBoardState(state);
  const selectedPathScore = detail.candidateLines.find((candidate) => candidate.selected)?.score
    ?? detail.candidateLines[0]?.score
    ?? 0;
  const phaseBase = {
    contested: 10,
    converged: 140,
    empty: -10,
    framing: 20,
    ready: 85,
  }[detail.evaluation.phase];
  const boardScore =
    phaseBase +
    selectedPathScore -
    detail.evaluation.attentionVector.forcingThreats * 24 -
    detail.evaluation.attentionVector.lineDebt * 18 -
    detail.evaluation.attentionVector.proofDebt * 10 -
    detail.evaluation.attentionVector.entropy * 4;

  if (depth <= 0 || detail.evaluation.phase === "converged") {
    cache.set(key, boardScore);
    return boardScore;
  }

  const nextActions = enumerateActions(sessionKey, state, detail).slice(0, BEAM_WIDTH);
  if (nextActions.length === 0) {
    cache.set(key, boardScore);
    return boardScore;
  }

  const childScores = nextActions.map((action) => {
    stats.exploredBranches += 1;
    return scoreAction(action, state, depth, sessionKey, stats, cache).score;
  });
  const futureBonus = Math.max(...childScores) * 0.35;
  const total = boardScore + futureBonus;
  cache.set(key, total);
  return total;
}

function enumerateActions(
  sessionKey: string,
  state: BoardState,
  detail: EvaluationDetail,
): PlannedAction[] {
  const atoms = Object.values(state.atoms);
  const actions: PlannedAction[] = [];
  const selectedPath = detail.mainline[0];

  if (!atoms.some((atom) => atom.kind === "problem")) {
    actions.push(createAtomAction(sessionKey, "problem/core", "problem", ":Define the core problem"));
  }
  if (!atoms.some((atom) => atom.kind === "goal")) {
    actions.push(createAtomAction(sessionKey, "goal/main", "goal", ":Define the target outcome"));
  }
  if (atoms.some((atom) => atom.kind === "problem") && atoms.some((atom) => atom.kind === "goal") &&
    !atoms.some((atom) => atom.kind === "path" && atom.status !== "archived" && atom.status !== "rejected")) {
    actions.push(createAtomAction(sessionKey, "path/mainline", "path", ":Describe the next executable line"));
  }

  if (state.line.selectedPathId === undefined && detail.candidateLines[0] !== undefined) {
    const best = detail.candidateLines[0];
    actions.push({
      action: {
        command: ["line", "select", sessionKey, "@tip", best.pathId],
        kind: "line.select-best",
        reason: `Select ${best.pathId} as the explicit mainline before doing more structural work.`,
      },
      apply: (draftState) => {
        selectLine(draftState, { pathId: best.pathId });
      },
    });
  }

  if (selectedPath === undefined) {
    return actions;
  }

  const betterCandidate = detail.candidateLines.find((candidate) =>
    candidate.pathId !== selectedPath.id && candidate.score >= (detail.candidateLines[0]?.score ?? 0)
  );
  if (
    betterCandidate !== undefined &&
    (detail.candidateLines.find((candidate) => candidate.pathId === selectedPath.id)?.score ?? -Infinity) + 16 <=
      betterCandidate.score
  ) {
    const reselectAction: PlannedAction = {
      action: {
        command: ["line", "select", sessionKey, "@tip", betterCandidate.pathId],
        kind: "line.reselect",
        reason: `${betterCandidate.pathId} currently dominates ${selectedPath.id}; reselect before adding more proof debt.`,
      },
      apply: (draftState) => {
        selectLine(draftState, { pathId: betterCandidate.pathId });
      },
    };
    actions.push(reselectAction);
    return [reselectAction];
  }

  const missingGoalIds = Object.values(state.atoms)
    .filter((atom) => atom.kind === "goal" && atom.status !== "archived" && atom.status !== "rejected")
    .filter((goal) =>
      !Object.values(state.cells).some((cell) =>
        cell.status === "active" &&
        cell.effect === "supports" &&
        cell.from === selectedPath.id &&
        cell.to === goal.id
      )
    )
    .map((goal) => goal.id)
    .sort();
  const hasTool = Object.values(state.cells).some((cell) =>
    cell.status === "active" && cell.effect === "uses" && cell.from === selectedPath.id
  );
  if (missingGoalIds.length > 0 || !hasTool) {
    const toolId = chooseToolId(state);
    const structureSweep: PlannedAction = {
      action: {
        command: collapseCommands([
          ...missingGoalIds.map((goalId) => ["cell", "upsert", sessionKey, "@tip", null, selectedPath.id, "supports", goalId]),
          ...(hasTool ? [] : [
            ...(state.atoms[toolId] === undefined
              ? [["atom", "upsert", sessionKey, "@tip", toolId, "tool", ":Name the primary tool or method for this path"]]
              : []),
            ["cell", "upsert", sessionKey, "@tip", null, selectedPath.id, "uses", toolId],
          ]),
        ]),
        kind: "line.structure-sweep",
        reason: describeStructureSweep(selectedPath, missingGoalIds, !hasTool),
      },
      apply: (draftState, helpers) => {
        for (const goalId of missingGoalIds) {
          upsertCell(draftState, helpers, {
            cellId: undefined,
            effect: "supports",
            from: selectedPath.id,
            to: goalId,
          });
        }
        if (!hasTool) {
          if (draftState.atoms[toolId] === undefined) {
            upsertAtom(draftState, helpers, {
              atomId: toolId,
              body: "Name the primary tool or method for this path",
              kind: "tool",
              status: undefined,
              tags: [],
              title: undefined,
            });
          }
          upsertCell(draftState, helpers, {
            cellId: undefined,
            effect: "uses",
            from: selectedPath.id,
            to: toolId,
          });
        }
      },
    };
    actions.push(structureSweep);
    return [structureSweep];
  }

  if (
    detail.evaluation.phase === "ready" &&
    detail.evaluation.attentionVector.forcingThreats === 0 &&
    detail.evaluation.attentionVector.lineDebt === 0
  ) {
    actions.push({
      action: {
        command: ["compact", sessionKey, "@tip", "state"],
        kind: "implement.draft",
        reason:
          `Board is ready on ${selectedPath.id}. Freeze a compact handoff and spend the next pass on bounded implementation plus external validation.`,
      },
      apply: () => {},
    });
  }

  const proofActions = detail.proofs.slice(0, 3).map((proof) => createProofAction(sessionKey, proof.targetAtomId, proof.summary));
  actions.push(...proofActions);

  return actions;
}

function createAtomAction(
  sessionKey: string,
  atomId: string,
  kind: AtomRecord["kind"],
  body: string,
): PlannedAction {
  return {
    action: {
      command: ["atom", "upsert", sessionKey, "@tip", atomId, kind, body],
      kind: "frame.atom",
      reason: `Create ${atomId} before searching deeper.`,
    },
    apply: (draftState, helpers) => {
      upsertAtom(draftState, helpers, {
        atomId,
        body: body.replace(/^:/, ""),
        kind,
        status: undefined,
        tags: [],
        title: undefined,
      });
    },
  };
}

function createProofAction(
  sessionKey: string,
  targetAtomId: string,
  reason: string,
): PlannedAction {
  return {
    action: {
      command: [
        "proof",
        "record",
        sessionKey,
        "@tip",
        targetAtomId,
        "pass",
        `:Close proof debt for ${targetAtomId} with concrete validation evidence.`,
      ],
      kind: "proof.close",
      reason,
    },
    apply: (draftState, helpers) => {
      recordProof(draftState, helpers, {
        body: `Close proof debt for ${targetAtomId} with concrete validation evidence.`,
        proofId: undefined,
        targetAtomId,
        verdict: "pass",
      });
    },
  };
}

function createVirtualMutationHelpers(state: BoardState): VirtualMutationHelpers {
  let atomCounter = 0;
  let cellCounter = 0;
  return {
    allocateAtomId(requestedId) {
      if (requestedId !== undefined) {
        return requestedId;
      }
      while (true) {
        atomCounter += 1;
        const candidate = `a_tmp${atomCounter}`;
        if (state.atoms[candidate] === undefined) {
          return candidate;
        }
      }
    },
    allocateCellId(requestedId) {
      if (requestedId !== undefined) {
        return requestedId;
      }
      while (true) {
        cellCounter += 1;
        const candidate = `c_tmp${cellCounter}`;
        if (state.cells[candidate] === undefined) {
          return candidate;
        }
      }
    },
  };
}

function collapseCommands(commands: Array<Array<string | null>>) {
  return commands.length === 1 ? commands[0] : commands;
}

function chooseToolId(state: BoardState) {
  return Object.values(state.atoms)
    .filter((atom) => atom.kind === "tool" && atom.status !== "archived" && atom.status !== "rejected")
    .sort((left, right) => left.id.localeCompare(right.id))[0]?.id ?? "tool/default";
}

function describeStructureSweep(
  selectedPath: AtomRecord,
  missingGoalIds: readonly string[],
  missingTool: boolean,
) {
  const parts: string[] = [];
  if (missingGoalIds.length > 0) {
    parts.push(`${missingGoalIds.length} missing goal link${missingGoalIds.length === 1 ? "" : "s"}`);
  }
  if (missingTool) {
    parts.push("the missing tool attachment");
  }
  return `Selected path ${selectedPath.id} still needs ${parts.join(" and ")}. Apply the whole structural sweep before re-evaluating.`;
}
